import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatApiErrorDetail } from "@/context/AuthContext";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

export function AdminSecurity({ open, onOpenChange, onPasswordChanged }) {
  const [enabled, setEnabled] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [setup, setSetup] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [disPwd, setDisPwd] = useState("");
  const [disCode, setDisCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setEnabled(!!data.totp_enabled);
    } catch (e) {}
  };

  useEffect(() => {
    if (open) {
      refresh();
      setSetup(null);
      setCur(""); setNw(""); setCf("");
      setEnableCode(""); setDisPwd(""); setDisCode("");
    }
  }, [open]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (nw !== cf) { toast.error("Le due password non coincidono"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password aggiornata. Effettua di nuovo il login.");
      onOpenChange(false);
      onPasswordChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const enable2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code: enableCode.trim() });
      toast.success("2FA attivata");
      setSetup(null); setEnableCode("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disPwd, code: disCode.trim() });
      toast.success("2FA disattivata");
      setDisPwd(""); setDisCode("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="security-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Sicurezza account
          </DialogTitle>
        </DialogHeader>

        <section className="space-y-4" data-testid="change-password-section">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
            <KeyRound className="h-4 w-4" /> Cambia password
          </h3>
          <form onSubmit={changePassword} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Password attuale</Label>
              <Input id="cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required autoComplete="current-password" data-testid="cur-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nw">Nuova password</Label>
              <Input id="nw" type="password" value={nw} onChange={(e) => setNw(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf">Ripeti nuova password</Label>
              <Input id="cf" type="password" value={cf} onChange={(e) => setCf(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={busy} data-testid="change-password-submit">Aggiorna password</Button>
          </form>
        </section>

        <div className="my-4 border-t border-border" />

        <section className="space-y-4" data-testid="twofa-section">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
            <ShieldCheck className="h-4 w-4" /> Autenticazione a due fattori (2FA)
          </h3>
          {enabled ? (
            <form onSubmit={disable2fa} className="space-y-3" data-testid="twofa-disable-form">
              <p className="text-sm text-muted-foreground">
                La 2FA è <span className="font-semibold text-foreground">attiva</span>. Per disattivarla inserisci la password e un codice valido (o di recupero).
              </p>
              <Input type="password" placeholder="Password" value={disPwd} onChange={(e) => setDisPwd(e.target.value)} required autoComplete="current-password" data-testid="disable-2fa-password" />
              <Input type="text" placeholder="Codice 2FA o di recupero" value={disCode} onChange={(e) => setDisCode(e.target.value)} required data-testid="disable-2fa-code" />
              <Button type="submit" variant="destructive" disabled={busy} data-testid="disable-2fa-submit">Disattiva 2FA</Button>
            </form>
          ) : setup ? (
            <div className="space-y-3" data-testid="twofa-setup-panel">
              <p className="text-sm text-muted-foreground">
                Scansiona il QR con Google Authenticator / Authy, poi inserisci il codice per confermare.
              </p>
              <img src={setup.qr} alt="QR 2FA" className="mx-auto h-44 w-44 rounded-lg border border-border" data-testid="twofa-qr" />
              <p className="break-all text-center text-xs text-muted-foreground">
                Codice manuale: <span className="font-mono" data-testid="twofa-secret">{setup.secret}</span>
              </p>
              <div className="rounded-lg bg-muted p-3" data-testid="recovery-codes">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Codici di recupero (salvali ora, mostrati una sola volta)
                </p>
                <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm">
                  {setup.recovery_codes.map((c) => (<span key={c}>{c}</span>))}
                </div>
              </div>
              <form onSubmit={enable2fa} className="space-y-2">
                <Input type="text" placeholder="Codice a 6 cifre" value={enableCode} onChange={(e) => setEnableCode(e.target.value)} required data-testid="enable-2fa-code" />
                <Button type="submit" disabled={busy} data-testid="enable-2fa-submit">Conferma e attiva</Button>
              </form>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                La 2FA <span className="font-semibold text-foreground">non è attiva</span>. Aggiungi un secondo livello di sicurezza al login.
              </p>
              <Button onClick={startSetup} disabled={busy} data-testid="start-2fa-setup">Attiva 2FA</Button>
            </div>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}
